import { useForm } from "@tanstack/react-form";
import { toast } from "sonner";
import { APPOINTMENT_NOTES_TOAST } from "@/lib/appointments/appointment-toast";

import { DataError } from "@/components/common/error/data-error";
import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import {
  CLIENT_HISTORY_PAGE_SIZE,
  CLIENT_HISTORY_STATUSES,
  HISTORY_NOTE_FIELDS,
} from "@/constants/clients/client-appointment-constants";
import { getClientAppointmentHistoryFormValues } from "@/default-form-values/clients/default-client-appointment-form-values";
import { useUpdateAppointment } from "@/hooks/appointments/use-appointments";
import { useGetSalesPaginated } from "@/hooks/sales/use-sales";
import { emptyToNull } from "@/mappers/clients/client-form-to-request";
import {
  clientAppointmentFormKey,
  earliestServiceStartTime,
  historyVisitLines,
  sortSalesNewestFirst,
} from "@/mappers/clients/client-appointment-display";
import type { Sale } from "@/types/sales/api-types/sale-types";
import { clientAppointmentHistorySchema } from "@/zod-schemas/clients/client-appointment-history-schema";
import { ClientAppointmentVisitBlock } from "./client-appointment-visit-block";

interface ClientAppointmentHistoryTabProps {
  clientId: number;
  onOpenChange: (open: boolean) => void;
}

export function ClientAppointmentHistoryTab({
  clientId,
  onOpenChange,
}: ClientAppointmentHistoryTabProps) {
  const { data, isPending, isError } = useGetSalesPaginated({
    page: 0,
    size: CLIENT_HISTORY_PAGE_SIZE,
    client_id: clientId,
    status: CLIENT_HISTORY_STATUSES,
  });

  if (isPending) {
    return (
      <div className="flex flex-1 items-center justify-center py-10">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (isError) {
    return <DataError topic="appointment history" />;
  }

  const sales = sortSalesNewestFirst(data?.items ?? []);
  if (sales.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <p className="flex-1 py-6 text-sm text-muted-foreground">
          No appointment history yet
        </p>
        <DialogFooter className="shrink-0 border-t pt-4">
          <DialogClose asChild>
            <Button variant="outline" className="w-24">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </div>
    );
  }

  return (
    <HistoryNotesForm
      key={clientAppointmentFormKey(sales.map((sale) => sale.id))}
      sales={sales}
      onOpenChange={onOpenChange}
    />
  );
}

interface HistoryNotesFormProps {
  sales: Sale[];
  onOpenChange: (open: boolean) => void;
}

function HistoryNotesForm({ sales, onOpenChange }: HistoryNotesFormProps) {
  const updateAppointment = useUpdateAppointment();
  const loadedValues = getClientAppointmentHistoryFormValues(sales);

  const form = useForm({
    defaultValues: loadedValues,
    validators: {
      onSubmit: clientAppointmentHistorySchema,
    },
    onSubmit: async ({ value }) => {
      const validated = clientAppointmentHistorySchema.parse(value);

      try {
        for (const item of validated.items) {
          const original = loadedValues.items.find(
            (loaded) => loaded.id === item.id
          );
          if (!original) continue;

          const notes = {
            colour_notes: emptyToNull(item.colour_notes),
            cut_notes: emptyToNull(item.cut_notes),
            product_notes: emptyToNull(item.product_notes),
            conversation_notes: emptyToNull(item.conversation_notes),
          };
          const unchanged = HISTORY_NOTE_FIELDS.every(
            ({ name }) => emptyToNull(original[name]) === notes[name]
          );
          if (unchanged) continue;

          await updateAppointment.mutateAsync({
            id: item.id,
            data: notes,
          });
        }
        toast.success(APPOINTMENT_NOTES_TOAST.success);
      } catch {
        toast.error(APPOINTMENT_NOTES_TOAST.error);
        return;
      }
      form.reset(validated);
    },
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <form
        id="client-appointment-history-form"
        onSubmit={(event) => {
          event.preventDefault();
          form.handleSubmit();
        }}
        className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto"
      >
        {sales.map((sale, index) => (
          <div key={sale.id}>
            {index > 0 ? <Separator className="mb-6" /> : null}
            <ClientAppointmentVisitBlock
              appointmentDate={sale.appointment_date}
              startTime={earliestServiceStartTime(sale.services)}
              status={sale.status}
              lines={historyVisitLines(sale)}
            >
              <FieldGroup className="mt-4 gap-3">
                {HISTORY_NOTE_FIELDS.map((noteField) => (
                  <form.Field
                    key={noteField.name}
                    name={`items[${index}].${noteField.name}`}
                    children={(field) => {
                      const isInvalid =
                        field.state.meta.isTouched && !field.state.meta.isValid;
                      return (
                        <Field data-invalid={isInvalid}>
                          <FieldLabel htmlFor={field.name}>
                            {noteField.label}
                          </FieldLabel>
                          <Textarea
                            id={field.name}
                            name={field.name}
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(e) => field.handleChange(e.target.value)}
                            aria-invalid={isInvalid}
                            rows={2}
                          />
                          {isInvalid && (
                            <FieldError errors={field.state.meta.errors} />
                          )}
                        </Field>
                      );
                    }}
                  />
                ))}
              </FieldGroup>
            </ClientAppointmentVisitBlock>
          </div>
        ))}
      </form>
      <DialogFooter className="shrink-0 border-t pt-4">
        <DialogClose asChild>
          <Button
            type="button"
            variant="outline"
            className="w-24"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
        </DialogClose>
        <form.Subscribe
          selector={(state) => [state.isDirty, state.isSubmitting] as const}
          children={([isDirty, isSubmitting]) => (
            <Button
              type="submit"
              form="client-appointment-history-form"
              className="w-24"
              disabled={!isDirty || isSubmitting}
            >
              Save
            </Button>
          )}
        />
      </DialogFooter>
    </div>
  );
}
